import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectRequestData, ResourceRequestData } from '../types';
import { saveSubmission } from '../utils/storage';
import ProjectRequestForm from './forms/ProjectRequestForm';
import ResourceRequestForm from './forms/ResourceRequestForm';

const CustomerRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'project' | 'resource'>('project');
  const [showSuccess, setShowSuccess] = useState(false);
  const [submissionId, setSubmissionId] = useState<string>('');

  const handleProjectSubmit = async (data: ProjectRequestData) => {
    try {
      const id = saveSubmission({
        type: 'project',
        status: 'new',
        data
      });
      setSubmissionId(id);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error saving project submission:', error);
      alert('Error submitting form. Please try again.');
    }
  };

  const handleResourceSubmit = async (data: ResourceRequestData) => {
    try {
      const id = saveSubmission({
        type: 'resource',
        status: 'new',
        data
      });
      setSubmissionId(id);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error saving resource submission:', error);
      alert('Error submitting form. Please try again.');
    }
  };

  const handleClear = () => {
    // This will be handled by individual form components
  };

  const closeSuccessModal = () => {
    setShowSuccess(false);
    setSubmissionId('');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-surface text-ink-700 pt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-ink-900">
            <span className="gradient-text">Partner Request</span>
          </h1>
          <p className="text-lg text-ink-500 max-w-2xl mx-auto">
            Tell us about your partnership or resource needs. We'll get back to you within 24 hours.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="glass-card p-2 mb-8">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('project')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all duration-300 ${
                activeTab === 'project'
                  ? 'bg-gradient-to-r from-brand-start to-brand-end text-white shadow-lg'
                  : 'text-ink-500 hover:text-ink-700 hover:bg-white/5'
              }`}
            >
              Project Request
            </button>
            <button
              onClick={() => setActiveTab('resource')}
              className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all duration-300 ${
                activeTab === 'resource'
                  ? 'bg-gradient-to-r from-brand-start to-brand-end text-white shadow-lg'
                  : 'text-ink-500 hover:text-ink-700 hover:bg-white/5'
              }`}
            >
              Resource Request
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="glass-card p-8">
          {activeTab === 'project' ? (
            <ProjectRequestForm onSubmit={handleProjectSubmit} onClear={handleClear} />
          ) : (
            <ResourceRequestForm onSubmit={handleResourceSubmit} onClear={handleClear} />
          )}
        </div>

        {/* Success Modal */}
        {showSuccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="glass-card max-w-md w-full text-center p-8 rounded-2xl shadow-2xl border border-white/10 animate-modalSlideIn">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-brand-start to-brand-end rounded-full mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold mb-2 text-ink-900">Request Submitted!</h3>
                <p className="text-ink-600 mb-4">
                  Thank you for your {activeTab === 'project' ? 'project' : 'resource'} request. 
                  We'll review it and get back to you within 24 hours.
                </p>
                <div className="bg-surface-soft rounded-lg p-3 mb-6">
                  <p className="text-sm text-ink-500">Submission ID:</p>
                  <p className="font-mono text-ink-700">{submissionId}</p>
                </div>
                <button
                  className="btn-primary"
                  onClick={closeSuccessModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add modal animation styles */}
      <style>{`
        @keyframes modalSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-modalSlideIn {
          animation: modalSlideIn 0.3s cubic-bezier(0.23, 1, 0.32, 1);
        }
      `}</style>
    </div>
  );
};

export default CustomerRequestPage;
